import { createAsyncThunk } from "@reduxjs/toolkit";
import gamesService from "../../../api/services/games";

// playCoinFlip — calls the backend coin flip. On reject we forward the
// HTTP status (e.g. 402 insufficient credits) so the page can branch on it,
// matching honeypot's ApiError.status handling.
export const playCoinFlip = createAsyncThunk(
  "games/playCoinFlip",
  async ({ bet, side }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playCoinFlip({ bet, side, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playDice = createAsyncThunk(
  "games/playDice",
  async ({ bet, threshold, direction }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playDice({ bet, threshold, direction, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playRollDice = createAsyncThunk(
  "games/playRollDice",
  async ({ bet }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playRollDice({ bet, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playScratch = createAsyncThunk(
  "games/playScratch",
  async ({ bet }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playScratch({ bet, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playSpin = createAsyncThunk(
  "games/playSpin",
  async ({ bet }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playSpin({ bet, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playDrop = createAsyncThunk(
  "games/playDrop",
  async ({ bet, ballCount }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playDrop({ bet, ballCount, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

// ─── Mines (multi-step) ────────────────────────────────────────────
export const getActiveMines = createAsyncThunk(
  "games/getActiveMines",
  async (_, { rejectWithValue }) => {
    try {
      return await gamesService.getActiveMines();
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const startMines = createAsyncThunk(
  "games/startMines",
  async ({ bet, waspCount }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.startMines({ bet, waspCount, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const revealMines = createAsyncThunk(
  "games/revealMines",
  async ({ roundId, cellIndex }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.revealMines({ roundId, cellIndex, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const cashoutMines = createAsyncThunk(
  "games/cashoutMines",
  async ({ roundId }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.cashoutMines({ roundId, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

// ─── Slots ──────────────────────────────────────────────────────────
export const playSlots = createAsyncThunk(
  "games/playSlots",
  async ({ bet }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playSlots({ bet, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const buySlotsBonus = createAsyncThunk(
  "games/buySlotsBonus",
  async ({ bet }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.buySlotsBonus({ bet, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const playSlotsGamble = createAsyncThunk(
  "games/playSlotsGamble",
  async ({ nonce, pick }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.playSlotsGamble({ nonce, pick, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

// ─── Bunny Crash (multi-step) ──────────────────────────────────────
export const getActiveBunnyCrash = createAsyncThunk(
  "games/getActiveBunnyCrash",
  async (_, { rejectWithValue }) => {
    try {
      return await gamesService.getActiveBunnyCrash();
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const startBunnyCrash = createAsyncThunk(
  "games/startBunnyCrash",
  async ({ bet, difficulty }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.startBunnyCrash({ bet, difficulty, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const stepBunnyCrash = createAsyncThunk(
  "games/stepBunnyCrash",
  async ({ roundId, expectedSteps }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.stepBunnyCrash({ roundId, expectedSteps, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);

export const cashoutBunnyCrash = createAsyncThunk(
  "games/cashoutBunnyCrash",
  async ({ roundId }, { rejectWithValue, requestId }) => {
    try {
      return await gamesService.cashoutBunnyCrash({ roundId, idempotencyKey: requestId });
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.status });
    }
  },
);
